import i18n from '../../../i18n/config';
// arsenal-tpms/frontend/src/components/vehicles/detail/SensorRegistrationDialog.jsx
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    Divider,
    Typography,
    CircularProgress,
    IconButton,
    Stack
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const SensorRegistrationDialog = ({
    open,
    onClose,
    onSubmit,
    loading = false
}) => {
    const { t } = useTranslation();
    const [sensorEntries, setSensorEntries] = useState([{ sensor_id: '' }]);
    const [formError, setFormError] = useState(null);

    const handleEntryChange = (index, value) => {
        setFormError(null);
        setSensorEntries((previous) => previous.map((entry, entryIndex) => (
            entryIndex === index ? { sensor_id: value } : entry
        )));
    };

    const handleAddEntry = () => {
        setFormError(null);
        setSensorEntries((previous) => [...previous, { sensor_id: '' }]);
    };

    const handleRemoveEntry = (index) => {
        setFormError(null);
        setSensorEntries((previous) => {
            if (previous.length === 1) {
                return previous;
            }
            return previous.filter((_, entryIndex) => entryIndex !== index);
        });
    };

    const handleClose = () => {
        setSensorEntries([{ sensor_id: '' }]);
        setFormError(null);
        onClose();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFormError(null);

        const sensors = sensorEntries
            .map((entry) => ({ sensor_id: entry.sensor_id.trim() }))
            .filter((entry) => entry.sensor_id);

        if (!sensors.length) {
            setFormError(t('hardcoded.components_sensors_sensorform.agrega_al_menos_un_sensor'));
            return;
        }

        const normalizedIds = sensors.map((entry) => entry.sensor_id.toUpperCase());
        if (new Set(normalizedIds).size !== normalizedIds.length) {
            setFormError(t('hardcoded.components_sensors_sensorform.no_puedes_registrar_ids_de'));
            return;
        }

        const result = await onSubmit({ sensors });
        if (result && !result.error) {
            handleClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {t('sensorForm.createTitle', 'Registrar Nuevo Sensor')}
            </DialogTitle>

            <form onSubmit={handleSubmit}>
                <DialogContent>
                    {formError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {formError}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('hardcoded.components_vehicles_detail_sensorregistrationdialog.registra_varios_sensores_fisicos_en')}</Typography>

                        <Divider />

                        <Stack spacing={2}>
                            {sensorEntries.map((entry, index) => (
                                <Box
                                    key={t('hardcoded.components_vehicles_detail_sensorregistrationdialog.vehicle-sensor-entry-index', { index: index })}
                                    sx={{
                                        p: 2,
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.sensor')}{index + 1}
                                        </Typography>
                                        {sensorEntries.length > 1 && (
                                            <IconButton color="error" onClick={() => handleRemoveEntry(index)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>

                                    <TextField
                                        fullWidth
                                        label={t('sensorForm.sensorId', 'ID del Sensor')}
                                        value={entry.sensor_id}
                                        onChange={(event) => handleEntryChange(index, event.target.value)}
                                        placeholder={t('sensorForm.sensorIdHelper', 'Ingresa el ID físico')}
                                    />
                                </Box>
                            ))}
                        </Stack>

                        <Button
                            type="button"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddEntry}
                        >
                            {i18n.t('hardcoded.components_sensors_sensorform.agregar_otro_sensor')}</Button>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2.5, pt: 1 }}>
                    <Button onClick={handleClose} disabled={loading} color="inherit">
                        {t('common.cancel', 'Cancelar')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                        sx={{ minWidth: 100 }}
                    >
                        {loading ? t('sensorForm.saving', 'Guardando...') : t('sensorForm.register', 'Registrar')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default SensorRegistrationDialog;